import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { pushToast } from '../../components/common/toast/toastStore';
import { userSafeErrorMessage } from '../../utils/error/userSafeError';
import {
  isSafePayUrl, BILLING_ERROR_CODES, openPortal, resumeSubscription, changePlan,
} from '../../services/billing/billingApi';
import { HANDOFF_MS, HANDOFF_FALLBACK_MS } from '../../utils/billing/handoffTiming';
import { billablePlanName } from '../../utils/billing/billablePlans';
import { withTimeout, isTimeoutError } from '../../utils/async/withTimeout';
import { ACTION_BEAT_TIMEOUT_MS } from '../ui/useModalActionBeat';
import { getNoticeReceiptsSession } from '../../utils/notices/noticeReceiptsSession';
import { markSubscribeCheckoutStarted } from '../../utils/billing/subscribeCheckoutMarker';

/** Leaves the app for Stripe. A function so tests can replace it. */
/** How long the status line says "Now on {plan}" after an immediate plan change. */
export const NOW_ON_MS = 2400;

export const goToStripe = (url) => { window.location.assign(url); };

/**
 * KAN-196 — the Plan & billing actions that are NOT a modal's own beat:
 * open the Stripe portal, resume, undo a scheduled plan change, plus the
 * open/closed state of the two modals. Failures toast through
 * `userSafeErrorMessage` (never raw text). One action at a time (`busy`).
 * Owner-only / owner-or-admin is decided by the caller from billingActions();
 * the server enforces it regardless.
 */
export default function useBillingActions({ status, reload }) {
  const intl = useIntl();
  const [busy, setBusy] = useState(null);
  const [chooser, setChooser] = useState(null); // null | 'subscribe' | 'change'
  const [cancelOpen, setCancelOpen] = useState(false);
  // KAN-852: Resume restarts billing, so the row opens ONE confirm that says what happens next.
  const [resumeOpen, setResumeOpen] = useState(false);
  // UNPAID_BALANCE from checkout: a calm notice with "Pay open bill" (payUrl may be null = no button).
  const [unpaid, setUnpaid] = useState(null);
  const busyRef = useRef(null);
  // KAN-852: null | 'checkout' | 'portal' — which hand-off modal shows (the chooser's checkout, or portal / pay-open-bill).
  const [handoff, setHandoff] = useState(null);
  // Round 3: the cancel flow's "Or switch to {plan}" opens the chooser with that plan picked.
  const [preselect, setPreselect] = useState(null);
  // Round 3: the "Now on {plan}" status line after an immediate (trial) plan change; clears itself.
  const [nowOn, setNowOn] = useState(null);
  const handoffTimers = useRef([]);
  const leavingRef = useRef(false); // a hand-off is in flight: never start a second one
  const nowOnTimer = useRef(null);
  useEffect(() => {
    const timers = handoffTimers.current;
    const back = (e) => { if (e.persisted) { leavingRef.current = false; setHandoff(null); } }; // back button from Stripe (bfcache)
    window.addEventListener('pageshow', back);
    return () => { window.removeEventListener('pageshow', back); timers.forEach(clearTimeout); if (nowOnTimer.current) clearTimeout(nowOnTimer.current); };
  }, []);

  /** Shows the hand-off state, waits ~700ms, then leaves for Stripe; if still here later, the card comes back. */
  const leaveForStripe = useCallback(async (url, kind = 'portal') => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setHandoff(kind);
    await new Promise((resolve) => { handoffTimers.current.push(setTimeout(resolve, HANDOFF_MS)); });
    goToStripe(url);
    handoffTimers.current.push(setTimeout(() => { leavingRef.current = false; setHandoff(null); }, HANDOFF_FALLBACK_MS));
  }, []);

  /** The chooser staged its modalHandoff rect and asks the host to swap it for the checkout hand-off (hard unmount, no exit beat). */
  const statusRef = useRef(status);
  statusRef.current = status;
  const leaveFromChooser = useCallback((url) => {
    // KAN-862: snapshot "had they subscribed before?" NOW (after activation the server always says yes), for the celebration.
    const { userId, tenantId } = getNoticeReceiptsSession();
    markSubscribeCheckoutStarted(userId, tenantId, { wasSubscribedBefore: statusRef.current?.hasPriorSubscription === true });
    setChooser(null);
    setPreselect(null);
    return leaveForStripe(url, 'checkout');
  }, [leaveForStripe]);

  const announceNowOn = useCallback((plan) => {
    if (nowOnTimer.current) clearTimeout(nowOnTimer.current);
    setNowOn(plan);
    nowOnTimer.current = setTimeout(() => { nowOnTimer.current = null; setNowOn(null); }, NOW_ON_MS);
  }, []);

  const toastError = useCallback((err, fallbackId, fallbackDefault) => {
    pushToast({
      type: 'error',
      message: userSafeErrorMessage(err, intl, { fallbackId, fallbackDefault, codes: BILLING_ERROR_CODES }),
    });
  }, [intl]);

  const run = useCallback(async (key, work, failId, failDefault) => {
    if (busyRef.current) return;
    busyRef.current = key;
    setBusy(key);
    try {
      await work();
    } catch (err) {
      toastError(err, failId, failDefault);
    } finally {
      busyRef.current = null;
      setBusy(null);
    }
  }, [toastError]);

  const portal = useCallback(() => run('portal', async () => {
    const { url } = await openPortal();
    if (typeof url !== 'string' || !url.startsWith('https://')) throw new Error('BILLING_FAILED');
    await leaveForStripe(url);
  }, 'billing.error.portal', "Couldn't open billing. Try again."), [run, leaveForStripe]);

  /** The confirm's Resume: true/false drives the dialog's action beat. The write is the truth; the refetch is non-fatal. */
  const confirmResume = useCallback(async () => {
    if (busyRef.current) return false;
    busyRef.current = 'resume';
    setBusy('resume');
    try {
      await withTimeout(resumeSubscription(), ACTION_BEAT_TIMEOUT_MS);
    } catch (err) {
      if (isTimeoutError(err)) {
        // The write may still land: tell the truth, free the dialog, and re-read the card (non-fatal).
        pushToast({ type: 'error', message: intl.formatMessage({ id: 'billing.resume.timeout', defaultMessage: "Couldn't confirm in time. Check the Billing card before trying again." }) });
        Promise.resolve(reload?.()).catch(() => {});
        return false;
      }
      toastError(err, 'billing.error.resume', "Couldn't resume the subscription. Try again.");
      return false;
    } finally {
      busyRef.current = null;
      setBusy(null);
    }
    // The Resume modal's done step is the confirmation (KAN-869) — no success toast here.
    // Round 3: no reload here — the Resume modal closes first (its exit beat), then re-reads the page (non-fatal).
    return true;
  }, [intl, toastError, reload]);

  // Choosing the current plan again removes the queued switch (billing-change-plan).
  const undoChange = useCallback(() => run('undo', async () => {
    await changePlan(status?.planCode);
    pushToast({ type: 'success', message: intl.formatMessage({ id: 'billing.toast.changeRemoved', defaultMessage: 'Plan change removed. You stay on {plan}.' }, { plan: billablePlanName(status, status?.planCode) || '' }) });
    await reload?.();
  }, 'billing.error.changePlan', "Couldn't change the plan. Try again."), [run, intl, reload, status]);

  return {
    busy,
    chooser,
    cancelOpen,
    resumeOpen,
    openResume: useCallback(() => setResumeOpen(true), []),
    closeResume: useCallback(() => setResumeOpen(false), []),
    confirmResume,
    openSubscribe: useCallback(() => setChooser('subscribe'), []),
    openChange: useCallback(() => setChooser('change'), []),
    closeChooser: useCallback(() => { setChooser(null); setPreselect(null); }, []),
    preselect,
    leaveFromChooser,
    nowOn,
    announceNowOn,
    /** Cancel step 2 -> chooser, with `code` picked (the cancel modal staged its modalHandoff rect first). */
    switchFromCancel: useCallback((code) => { setCancelOpen(false); setPreselect(code || null); setChooser('change'); }, []),
    unpaid,
    showUnpaid: useCallback((payUrl) => setUnpaid({ payUrl: isSafePayUrl(payUrl) ? payUrl : null }), []),
    clearUnpaid: useCallback(() => setUnpaid(null), []),
    payOpenBill: useCallback(() => {
      if (leavingRef.current || !isSafePayUrl(unpaid?.payUrl)) return;
      void leaveForStripe(unpaid.payUrl);
    }, [unpaid, leaveForStripe]),
    handoff,
    openCancel: useCallback(() => setCancelOpen(true), []),
    closeCancel: useCallback(() => setCancelOpen(false), []),
    portal,
    resume: useCallback(() => setResumeOpen(true), []),
    undoChange,
    toastError,
  };
}
